import React, { useState, useCallback, useEffect } from 'react';

import { useDictionary, usePage } from '@/src/app/hooks';
import {
    Loading,
    ErrorMessage,
    ResourceBoxInner,
    Container,
    type ResourceHeaderDTO,
} from '@/src/ui';
import { apiRequest } from '@/src/services';
import './BTBFPage.css';

const BTBFPage: React.FC = () => {
    const { translate } = useDictionary();
    const { setSubheaderTitle, setSubheaderType, pageLanguage } = usePage();
    document.title = translate('BTBF', 'BTBF');

    useEffect(() => {
        setSubheaderTitle(translate('BTBF'));
        setSubheaderType('default');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translate]);

    const [btbfData, setBtbfData] = useState<ResourceHeaderDTO[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        setError('');
        setLoading(true);
        setBtbfData(null);
        try {
            const res = await apiRequest<ResourceHeaderDTO[]>(
                'GET',
                `dbo/btbf?language=${pageLanguage}`
            );
            setBtbfData(res.data);
        } catch {
            setError('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    }, [pageLanguage]);
    useEffect(() => {
        (async function init() {
            fetchData();
        })();
    }, [fetchData]);

    const renderColumn = useCallback(
        (colNo: number) => {
            const boxes = btbfData?.filter((box) => box.columnNumber === colNo) || [];

            return (
                <>
                    {boxes.map((box) => (
                        <div id={box.id} className={`btbf-box background pd-primary`}>
                            <div className="tile-headings">{box.title}</div>
                            {box.items.map((item) => {
                                const anchorId = `assessments-container-c${colNo}-${item.id}`;
                                return (
                                    <ResourceBoxInner
                                        key={anchorId}
                                        id={anchorId}
                                        item={item}
                                        namespace="btbf"
                                    />
                                );
                            })}
                        </div>
                    ))}
                </>
            );
        },
        [btbfData]
    );

    return (
        <Container>
            <ErrorMessage error={error} />
            <h2 className="resourcesTitle-text pd-primary text">
                {translate('Beyond the Basic Facts')}
            </h2>
            {loading ? (
                <Loading />
            ) : (
                <div className="btbf-container">
                    <div className="row gx-5 justify-content-center" id="dashboard">
                        {[1, 2, 3].map((num) => {
                            const hasData = btbfData?.some((box) => box.columnNumber === num);

                            // Only render the div if data exists for this column
                            return hasData ? (
                                <div key={num} id={`column_${num}`} className="col-4 btbf-column">
                                    {renderColumn(num)}
                                </div>
                            ) : null;
                        })}
                    </div>
                </div>
            )}
        </Container>
    );
};

export default BTBFPage;
